import { avatarSrc } from '../../utils/employeeAvatar';
import { Alert, Dropdown, Menu, MenuButton, MenuItem } from '@mui/joy';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import {
  Workspace,
  WorkspaceHeader,
  SummaryCards,
  WorkspaceTabs,
} from '../../components/Workspace/Workspace';
import { formatRating } from '../../utils/rating';
import PerformerPrograms from './PerformerPrograms';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Stack,
  Table,
  Typography,
  Sheet,
  ModalClose,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Checkbox,
  Textarea,
  CircularProgress,
  Tooltip,
} from '@mui/joy';
import StarIcon from '@mui/icons-material/Star';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { LayoutContext } from '../../context';
import Cookies from 'js-cookie';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import LoadingBlockGif from '../../components/LoadingBlockGif';
import {
  APIBlockUnblockEmployee,
  APIGetEmployeesPaginated,
  APICreateChatWithEmployee,
  APIApproveRegistration,
  APIRejectRegistration,
} from '../../api';
import { track } from '../../utils/tracker';
import { useNavigate } from 'react-router';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import { accountHasPermission } from '../../roles';

export default function PerformersPage() {
  const navigate = useNavigate();

  const { employeeAll, tokenEmployee, setEmployeeAll, getEmployeeAll, setModalEmployee, account } =
    useContext(LayoutContext);

  // Гранулярные права раздела «Исполнители»
  const canCreate = accountHasPermission(account, 'performers.create');
  const canEdit = accountHasPermission(account, 'performers.edit');
  const [filteredEmployees, setFilteredEmployees] = useState([]);

  // Пагинация
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loadError, setLoadError] = useState('');
  const tableRef = useRef(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Защита от гонок и параллельных запросов
  const inFlightRef = useRef(false);
  const requestIdRef = useRef(0);
  const abortRef = useRef(null);
  const isFirstLoadRef = useRef(true);

  // Состояния для фильтров
  const [filters, setFilters] = useState({
    search: '',
    position: '',
    city: '',
    timezone: '',
    status: 'all',
    no_coverage: false,
  });

  // Debounce для поиска
  const searchTimerRef = useRef(null);

  const [writingId, setWritingId] = useState(null);

  // Загрузка страницы исполнителей с сервера
  const loadEmployees = useCallback(
    async (pageNum = 1, append = false) => {
      const token = tokenEmployee || Cookies.get('auth_token');
      if (!token) {
        setInitialLoading(false);
        return;
      }
      setLoadError('');

      // Не запускаем второй запрос, пока бежит предыдущий
      if (inFlightRef.current) return;

      // Отмена прошлого запроса (если жив)
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Метка текущего запроса, чтобы поздний ответ не перезаписал свежие данные
      const myRequestId = ++requestIdRef.current;

      inFlightRef.current = true;
      if (pageNum > 1) setLoadingMore(true);

      try {
        const data = await APIGetEmployeesPaginated(token, {
          page: pageNum,
          page_size: 20,
          search: filters.search,
          position: filters.position,
          no_coverage: filters.no_coverage ? '1' : undefined,
          city: filters.city,
          // 'all' = «без фильтра», на бэк это слать не нужно
          status: filters.status && filters.status !== 'all' ? filters.status : '',
          signal: controller.signal,
        });

        // Если за это время улетел более новый запрос — игнорируем этот ответ
        if (myRequestId !== requestIdRef.current) return;

        if (!data) throw new Error('Пустой ответ сервера');
        if (data) {
          if (append) {
            // На всякий случай дедуплицируем по id
            setFilteredEmployees((prev) => {
              const map = new Map(prev.map((e) => [e.id, e]));
              (data.results || []).forEach((e) => map.set(e.id, e));
              return Array.from(map.values());
            });
          } else {
            setFilteredEmployees(data.results || []);
            // Прокрутим таблицу наверх — это новый набор результатов
            if (tableRef.current) tableRef.current.scrollTop = 0;
          }
          setHasNext(!!data.has_next);
          setTotal(data.total || 0);
          setSummary(data.summary || null);
          setPage(pageNum);
        }
      } catch (e) {
        if (e?.name !== 'CanceledError' && e?.name !== 'AbortError') {
          if (myRequestId === requestIdRef.current)
            setLoadError('Не удалось загрузить исполнителей. Повторите запрос.');
        }
      } finally {
        if (myRequestId === requestIdRef.current) {
          inFlightRef.current = false;
          setLoadingMore(false);
          setInitialLoading(false);
        }
      }
    },
    [tokenEmployee, filters],
  );

  // Первичная загрузка + перезагрузка при смене фильтров
  useEffect(() => {
    // Сразу сбрасываем состояние пагинации, чтобы scroll-обработчик
    // не успел дёрнуть «следующую страницу» со старым page до того, как
    // прилетит ответ нового запроса
    abortRef.current?.abort();
    ++requestIdRef.current;
    setPage(1);
    setHasNext(false);
    inFlightRef.current = false; // отпустим замок, прошлый запрос уже не нужен

    // Первый раз — без задержки, чтобы не тормозить старт
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      loadEmployees(1, false);
      return;
    }

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      loadEmployees(1, false);
    }, 300);

    return () => clearTimeout(searchTimerRef.current);
  }, [filters, tokenEmployee]);

  useEffect(
    () => () => {
      ++requestIdRef.current;
      abortRef.current?.abort();
      clearTimeout(searchTimerRef.current);
    },
    [],
  );

  // Также загружаем полный список для фильтров (города) — один раз
  useEffect(() => {
    if (!employeeAll || employeeAll.length === 0) {
      getEmployeeAll();
    }
  }, []);

  // Infinite scroll
  useEffect(() => {
    const container = tableRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        scrollHeight - scrollTop - clientHeight < 100 &&
        hasNext &&
        !loadingMore &&
        !inFlightRef.current // <-- НЕ грузим, пока в полёте поиск/предыдущая страница
      ) {
        loadEmployees(page + 1, true);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasNext, loadingMore, page, loadEmployees]);

  // Обработчик сохранения исполнителя (после создания/редактирования)
  function handleEmployeeSave(updatedEmployee) {
    if (updatedEmployee) {
      loadEmployees(1, false);
      getEmployeeAll(); // обновим и полный список для фильтра городов
    }
  }

  const handleApprove = async (employee) => {
    const token = tokenEmployee || Cookies.get('auth_token');
    if (!token) return;
    const res = await APIApproveRegistration(employee.id, token);
    if (res?.status === 'ok') {
      track('frontend.performer.approve', {
        title: `Одобрил анкету исполнителя #${employee.id}`,
        meta: { employee_id: employee.id },
      });
      loadEmployees(1, false);
    } else alert('Не удалось подтвердить анкету');
  };

  const handleReject = async (employee) => {
    if (!window.confirm('Отклонить анкету этого исполнителя?')) return;
    const token = tokenEmployee || Cookies.get('auth_token');
    if (!token) return;
    const res = await APIRejectRegistration(employee.id, token);
    if (res?.status === 'ok') {
      track('frontend.performer.reject', {
        title: `Отклонил анкету исполнителя #${employee.id}`,
        level: 'warning',
        meta: { employee_id: employee.id },
      });
      loadEmployees(1, false);
    } else alert('Не удалось отклонить анкету');
  };

  const handleWrite = async (employee) => {
    const token = tokenEmployee || Cookies.get('auth_token');
    if (!token) return;
    try {
      setWritingId(employee.id);
      const res = await APICreateChatWithEmployee(employee.id, token);
      if (res?.chat_id) navigate(`/chats?chatId=${res.chat_id}`);
      else alert('Не удалось открыть чат');
    } catch (e) {
      console.error('Ошибка открытия чата:', e);
      alert('Не удалось открыть чат');
    } finally {
      setWritingId(null);
    }
  };

  // Обработчики действий
  const handleEdit = (employee) => {
    setModalEmployee({
      open: true,
      employee: employee,
      onSave: handleEmployeeSave,
    });
  };

  const handleCreate = () => {
    setModalEmployee({
      open: true,
      employee: null,
      onSave: handleEmployeeSave,
    });
  };

  const handleDelete = (employee) => {
    if (
      window.confirm(
        `Вы уверены, что хотите удалить исполнителя ${employee?.last_name} ${employee.name} ${employee?.patronymic}?`,
      )
    ) {
      // TODO: Реализовать вызов API удаления
      console.log('Удаление исполнителя:', employee);
    }
  };

  const handleBlock = async (employee) => {
    const action = employee.is_block ? 'разблокировать' : 'заблокировать';

    if (
      window.confirm(
        `Вы уверены, что хотите ${action} исполнителя ${employee?.last_name} ${employee.name} ${employee?.patronymic}?`,
      )
    ) {
      try {
        // Получаем токен из контекста
        const token = tokenEmployee; // или из вашего контекста

        // Вызываем API для блокировки/разблокировки
        const result = await APIBlockUnblockEmployee(
          employee.id,
          employee.is_block ? 'unblock' : 'block',
          token,
        );

        if (result.status === 'ok') {
          track(employee.is_block ? 'frontend.performer.unblock' : 'frontend.performer.block', {
            title: `${employee.is_block ? 'Разблокировал' : 'Заблокировал'} исполнителя #${employee.id}`,
            level: employee.is_block ? 'info' : 'warning',
            meta: { employee_id: employee.id },
          });
          // Обновляем локальное состояние
          setFilteredEmployees((prev) =>
            prev.map((emp) => (emp.id === employee.id ? { ...emp, is_block: !employee.is_block } : emp)),
          );

          // Показываем уведомление об успехе
          alert(
            result.message ||
              `Исполнитель успешно ${action === 'заблокировать' ? 'заблокирован' : 'разблокирован'}`,
          );
        } else {
          alert('Ошибка: ' + (result.message || 'Неизвестная ошибка'));
        }
      } catch (error) {
        console.error('Ошибка при блокировке исполнителя:', error);
        alert('Произошла ошибка при выполнении операции');
      }
    }
  };

  // Функция для отображения позиций Исполнителя
  const renderPositions = (positions) => {
    if (!positions || positions.length === 0) return '-';

    const positionColors = {
      surveyor: 'primary',
      manager: 'success',
      draftsman: 'warning',
      quality_master: 'neutral',
    };

    return (
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {positions.map((position, index) => (
          <Chip key={index} size="sm" variant="soft" color={positionColors[position] || 'neutral'}>
            {getPositionDisplayName(position)}
          </Chip>
        ))}
      </Stack>
    );
  };

  const getPositionDisplayName = (positionCode) => {
    const positionNames = {
      surveyor: 'Обмерщик',
      manager: 'Менеджер',
      draftsman: 'Чертежник',
      quality_master: 'Мастер качества',
    };
    return positionNames[positionCode] || positionCode;
  };

  return (
    <Workspace className="studio-performers">
      <WorkspaceHeader title="Исполнители" subtitle="Команда, опыт и доступ к системе">
        {canCreate && (
          <Button startDecorator={<AddRoundedIcon />} onClick={handleCreate}>
            Добавить исполнителя
          </Button>
        )}
      </WorkspaceHeader>
      <SummaryCards
        items={[
          {
            label: 'Всего в выборке',
            value: total,
            icon: <GroupsRoundedIcon />,
          },
          {
            label: 'Обмерщики',
            value: summary?.surveyors,
            icon: <PlaceRoundedIcon />,
          },
          {
            label: 'Чертёжники',
            value: summary?.draftsmen,
            icon: <DescriptionRoundedIcon />,
          },
          {
            label: 'Без зоны покрытия',
            value: summary?.without_coverage,
            icon: <PlaceRoundedIcon />,
            tone: 'warning',
          },
        ]}
      />
      <Box className="studio-panel">
        <WorkspaceTabs
          value={filters.position}
          onChange={(position) => setFilters((p) => ({ ...p, position }))}
          items={[
            { value: '', label: 'Все' },
            { value: 'surveyor', label: 'Обмерщики' },
            { value: 'draftsman', label: 'Чертёжники' },
            { value: 'manager', label: 'Менеджеры' },
            { value: 'quality_master', label: 'Мастера качества' },
          ]}
        />
        <Box className="studio-toolbar">
          <Input
            aria-label="Поиск исполнителей"
            placeholder="Имя, телефон, почта, город или ID"
            value={filters.search}
            onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
            startDecorator={<SearchRoundedIcon />}
          />
          <Select
            aria-label="Город исполнителя"
            placeholder="Все города"
            renderValue={(selected) => selected?.label || 'Все города'}
            value={filters.city}
            onChange={(_, city) => setFilters((p) => ({ ...p, city: city || '' }))}
            sx={{ minWidth: 150 }}
          >
            <Option value="">Все города</Option>
            {[
              ...new Set(
                (employeeAll || [])
                  .map((e) => e.city_bitrix_name || e.city)
                  .filter((v) => typeof v === 'string'),
              ),
            ]
              .sort((a, b) => a.localeCompare(b, 'ru'))
              .map((city) => (
                <Option key={city} value={city}>
                  {city}
                </Option>
              ))}
          </Select>
          <Select
            aria-label="Статус исполнителя"
            value={filters.status}
            onChange={(_, status) => status && setFilters((p) => ({ ...p, status }))}
          >
            <Option value="all">Все статусы</Option>
            <Option value="active">Активные</Option>
            <Option value="blocked">Заблокированные</Option>
            <Option value="pending">На проверке</Option>
          </Select>
          <Checkbox
            label="Без зон покрытия"
            size="sm"
            checked={!!filters.no_coverage}
            onChange={(e) => setFilters((p) => ({ ...p, no_coverage: e.target.checked }))}
          />
          <Button
            size="sm"
            variant="plain"
            onClick={() =>
              setFilters({
                search: '',
                position: '',
                city: '',
                timezone: '',
                status: 'all',
                no_coverage: false,
              })
            }
          >
            Сбросить
          </Button>
        </Box>
        {loadError && (
          <Alert
            color="danger"
            endDecorator={
              <Button variant="plain" onClick={() => loadEmployees(1, false)}>
                Повторить
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}
        <Sheet
          ref={tableRef}
          className="studio-table-scroll"
          sx={{ maxHeight: '65dvh', minHeight: 220 }}
          aria-busy={initialLoading || loadingMore}
        >
          <Table
            className="studio-table"
            stickyHeader
            hoverRow
            borderAxis="xBetween"
            sx={{
              '--TableCell-paddingY': '16px',
              '--TableCell-paddingX': '18px',
            }}
          >
            <colgroup>
              <col style={{ width: '27%' }} />
              <col style={{ width: '19%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: 50 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Исполнитель</th>
                <th>Роль и программы</th>
                <th>Рейтинг</th>
                <th>Опыт</th>
                <th>Город и зона</th>
                <th>Доступ</th>
                <th aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee) => (
                <tr key={employee.id}>
                  <td>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Avatar src={avatarSrc(employee)} size="sm" variant="soft" color="primary">
                        {employee.name?.charAt(0)}
                        {employee.last_name?.charAt(0)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <button
                          className="studio-name-button"
                          title={`Исполнитель №${employee.id}`}
                          onClick={() => canEdit && handleEdit(employee)}
                          disabled={!canEdit}
                        >
                          {employee.last_name} {employee.name} {employee.patronymic}
                        </button>
                        <Stack direction="row" gap={1} flexWrap="wrap">
                          {employee.phone && (
                            <a className="studio-link" href={`tel:${employee.phone.replace(/[^+\d]/g, '')}`}>
                              {employee.phone}
                            </a>
                          )}
                          {employee.email && (
                            <a className="studio-link" href={`mailto:${employee.email}`}>
                              {employee.email}
                            </a>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </td>
                  <td data-label="Роль и программы">
                    {renderPositions(employee.position)}
                    <PerformerPrograms programs={employee.drawing_programs} />
                  </td>
                  <td data-label="Рейтинг">
                    {employee.rating != null ? (
                      <>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <StarIcon sx={{ fontSize: 17, color: '#f5a623' }} />
                          <Typography level="title-sm">{formatRating(employee.rating)}</Typography>
                        </Stack>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                          Отзывов: {employee.reviews_count ?? 0}
                        </Typography>
                      </>
                    ) : (
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        Нет оценок
                      </Typography>
                    )}
                  </td>
                  <td data-label="Опыт">
                    <Typography level="title-sm">
                      {employee.surveyor_stats?.completed_count ?? employee.events_count ?? 0}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {employee.surveyor_stats ? 'обмеров' : 'событий всего'}
                    </Typography>
                  </td>
                  <td data-label="Город и зона">
                    <Typography level="body-sm">
                      {employee.city_bitrix_name || employee.city || '—'}
                    </Typography>
                    {employee.position?.includes('surveyor') && (
                      <Typography
                        level="body-xs"
                        sx={{
                          color: employee.coverage_zones_count ? 'text.tertiary' : 'warning.plainColor',
                        }}
                      >
                        Зон покрытия: {employee.coverage_zones_count ?? 0}
                      </Typography>
                    )}
                  </td>
                  <td data-label="Доступ">
                    <Chip
                      size="sm"
                      variant="soft"
                      color={
                        employee.is_block
                          ? 'warning'
                          : employee.is_confirmed === false
                            ? 'neutral'
                            : 'success'
                      }
                    >
                      {employee.is_block
                        ? 'Заблокирован'
                        : employee.is_confirmed === false
                          ? 'На проверке'
                          : 'Активен'}
                    </Chip>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                      Push: {employee.has_push ? 'включены' : 'выключены'}
                    </Typography>
                  </td>
                  <td>
                    <Dropdown>
                      <MenuButton
                        slots={{ root: IconButton }}
                        slotProps={{
                          root: {
                            size: 'sm',
                            variant: 'plain',
                            'aria-label': `Действия: ${employee.last_name || employee.name}`,
                          },
                        }}
                      >
                        <MoreHorizRoundedIcon />
                      </MenuButton>
                      <Menu sx={{ zIndex: 10020 }}>
                        <MenuItem onClick={() => handleWrite(employee)} disabled={writingId === employee.id}>
                          <ChatRoundedIcon />
                          Написать
                        </MenuItem>
                        {canEdit && (
                          <MenuItem onClick={() => handleEdit(employee)}>
                            <EditRoundedIcon />
                            Карточка исполнителя
                          </MenuItem>
                        )}
                        {employee.is_confirmed === false && canCreate && (
                          <MenuItem onClick={() => handleApprove(employee)}>
                            <CheckRoundedIcon />
                            Подтвердить анкету
                          </MenuItem>
                        )}
                        {employee.is_confirmed === false && canEdit && (
                          <MenuItem onClick={() => handleReject(employee)}>
                            <CloseRoundedIcon />
                            Отклонить анкету
                          </MenuItem>
                        )}
                        {canEdit && (
                          <MenuItem onClick={() => handleBlock(employee)}>
                            {employee.is_block ? <LockOpenRoundedIcon /> : <BlockRoundedIcon />}
                            {employee.is_block ? 'Разблокировать' : 'Заблокировать'}
                          </MenuItem>
                        )}
                      </Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {!filteredEmployees.length && (
            <Box className="studio-empty">
              <Typography level="title-md">
                {initialLoading ? 'Загрузка исполнителей…' : 'Исполнители не найдены'}
              </Typography>
            </Box>
          )}
        </Sheet>
        <Box className="studio-footer">
          <Typography level="body-sm">
            Загружено {filteredEmployees.length} из {total}
          </Typography>
          {hasNext && (
            <Button
              size="sm"
              variant="soft"
              loading={loadingMore}
              onClick={() => loadEmployees(page + 1, true)}
            >
              Показать ещё
            </Button>
          )}
        </Box>
      </Box>
    </Workspace>
  );
}
